import { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api } from "../lib/api";
import { TRAIT_LABELS, TRAITS } from "../data/philosophers";
import { gradeFor } from "../lib/scoring";
import { track } from "../lib/analytics";
import transformReadingPathLinks from "../lib/reportLinks";
import SEOHead from "../components/SEOHead";

// Custom renderer for links: opens in a new tab with sponsored rel, and fires
// a "book_click" analytics event carrying the book title so we can measure
// affiliate CTR in /admin/stats. Defined inside the component so we can close
// over the current result_id without touching module state.
export default function ReportPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const key = params.get("key");
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [polling, setPolling] = useState(false);

  const markdownComponents = useMemo(() => ({
    a: ({ node, href, ...props }) => (
      <a
        {...props}
        href={href}
        target="_blank"
        rel="noopener sponsored"
        data-testid="affiliate-book-link"
        onClick={() => {
          try {
            const title = new URL(href).searchParams.get("k") || "";
            if (title) track("book_click", { title, result_id: id });
          } catch (_e) {
            // Never block navigation on an analytics failure.
          }
        }}
      />
    ),
  }), [id]);

  // Rendered markdown = original + Amazon-affiliate links in the reading path.
  // The stored markdown is NEVER mutated — this is a pure render-time transform.
  // Hook must live above any conditional returns to comply with Rules of Hooks.
  const renderedMarkdown = useMemo(
    () => transformReadingPathLinks(data?.report_markdown, data?.affiliate_tag, data?.books),
    [data?.report_markdown, data?.affiliate_tag, data?.books],
  );

  useEffect(() => {
    let cancelled = false;
    let intervalId;

    const fetchOnce = async () => {
      try {
        const r = await api.getReport(id, key);
        if (cancelled) return;
        setData(r);
        setErr(null);
        if (r.report_status === "ready" && r.report_markdown) {
          track("report_view", { result_id: id });
          if (intervalId) clearInterval(intervalId);
          setPolling(false);
        } else if (r.report_status === "failed") {
          if (intervalId) clearInterval(intervalId);
          setPolling(false);
        } else {
          setPolling(true);
        }
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 403 || status === 404) {
          setErr({ code: status, msg: e?.response?.data?.detail || "Access denied." });
          if (intervalId) clearInterval(intervalId);
        }
      }
    };

    fetchOnce();
    intervalId = setInterval(fetchOnce, 3000);
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [id, key]);

  // Access denied → paywall
  if (err && (err.code === 403 || err.code === 404)) {
    return (
      <div className="relative z-10 mx-auto min-h-[100dvh] w-full max-w-md px-6 py-20 text-center" data-testid="report-paywall">
        <p className="text-[11px] font-medium uppercase" style={{ color: "var(--muted)", letterSpacing: "0.32em" }}>
          Full Report — Locked
        </p>
        <h1 className="mt-3 font-serif text-[34px] leading-[1.15]" style={{ color: "var(--gold)" }}>
          This report is private.
        </h1>
        <p className="mt-4 text-[15px]" style={{ color: "var(--text)" }}>
          {err.msg} You need a valid access link to view it — either from your purchase confirmation or your email.
        </p>
        <div className="mt-10 flex flex-col gap-3">
          <button className="btn-primary" onClick={() => navigate(`/result/${id}`)} data-testid="go-to-result-btn">
            Open the free result
          </button>
          <button className="btn-ghost" onClick={() => navigate("/")}>
            Back to home
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="relative z-10 mx-auto min-h-[100dvh] max-w-md px-6 py-24 text-center">
        <p style={{ color: "var(--muted)" }}>Loading…</p>
      </div>
    );
  }

  if (data.report_status === "failed") {
    return (
      <div className="relative z-10 mx-auto min-h-[100dvh] w-full max-w-md px-6 py-20 text-center" data-testid="report-failed">
        <h1 className="font-serif text-[30px]" style={{ color: "var(--gold)" }}>
          We couldn&apos;t generate your report.
        </h1>
        <p className="mt-4 text-[15px]" style={{ color: "var(--text)" }}>
          Your payment is safe. Please email <a href="mailto:support@thephilosophertest.com" style={{ color: "var(--gold)" }}>support@thephilosophertest.com</a> and we&apos;ll deliver it manually.
        </p>
      </div>
    );
  }

  if (data.report_status !== "ready" || !data.report_markdown) {
    return (
      <div className="relative z-10 mx-auto min-h-[100dvh] max-w-md px-6 py-24 text-center" data-testid="report-generating">
        <div className="mx-auto flex justify-center">
          <div className="pulse-core h-14 w-14 rounded-full" style={{
            background: "radial-gradient(circle, var(--gold-light) 0%, var(--gold) 55%, rgba(201,167,85,0.4) 100%)",
          }} />
        </div>
        <p className="mt-8 font-serif italic" style={{ color: "var(--gold-light)" }}>
          Writing your report…
        </p>
        <p className="mt-2 text-[12px]" style={{ color: "var(--muted)" }}>
          This takes about 30 seconds. This page updates automatically.
        </p>
        {polling && <p className="sr-only">polling</p>}
      </div>
    );
  }

  const traitsSorted = TRAITS.map((t) => ({
    key: t,
    label: TRAIT_LABELS[t],
    value: data.traits[t],
    grade: gradeFor(data.traits[t]),
  })).sort((a, b) => b.value - a.value);

  return (
    <div className="relative z-10 mx-auto min-h-[100dvh] w-full px-5 py-14 report-root" data-testid="report-screen">
      <SEOHead noindex path={`/report/${id || ""}`} title="Full Report" />
      <div style={{ maxWidth: 680, margin: "0 auto" }}>
        <p className="text-[11px] font-medium uppercase" style={{ color: "var(--muted)", letterSpacing: "0.32em" }}>
          The Philosopher Test · Full Report
        </p>
        <h1 className="mt-3 font-serif text-[38px] sm:text-[46px] leading-[1.05]" style={{ color: "var(--gold)" }}>
          {data.philosopher.charAt(0).toUpperCase() + data.philosopher.slice(1)} — {data.match_pct}% match
        </h1>

        {/* Trait bars header */}
        <div className="panel mt-8 no-print-shadow">
          <p className="text-[10px] font-medium uppercase" style={{ color: "var(--gold)", letterSpacing: "0.32em" }}>
            Your character sheet
          </p>
          <div className="mt-4 flex flex-col gap-3">
            {traitsSorted.map((t) => (
              <div key={t.key} className="flex items-center gap-4">
                <div className="w-24 shrink-0 font-serif text-[14px]" style={{ color: "var(--text)" }}>
                  {t.label}
                </div>
                <div className="trait-track flex-1">
                  <div className="trait-fill" style={{ width: `${Math.max(4, Math.round(t.value))}%` }} />
                </div>
                <div className="w-9 shrink-0 text-left font-serif text-lg tabular-nums" style={{ color: "var(--gold-light)" }}>
                  {t.grade}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="report-body mt-12">
          <ReactMarkdown components={markdownComponents}>
            {renderedMarkdown}
          </ReactMarkdown>
        </div>

        <p
          className="mt-8 text-[12px] italic leading-relaxed"
          style={{ color: "var(--muted)" }}
          data-testid="affiliate-disclosure"
        >
          As an Amazon Associate, we earn from qualifying purchases. Book links on this
          page are affiliate links.
        </p>

        <div className="mt-12 flex flex-col gap-3 no-print">
          <button className="btn-primary" onClick={() => window.print()} data-testid="print-pdf-btn">
            Download as PDF
          </button>
          <button className="btn-ghost" onClick={() => navigate(`/result/${id}`)}>
            Back to my result
          </button>
        </div>
      </div>
    </div>
  );
}
